import { Checkbox, FormControlLabel, InputAdornment, TextField } from "@mui/material";
import de from "date-fns/esm/locale/de";
import { Timestamp } from "firebase/firestore";
import { cloneDeep } from "lodash";
import { useSnackbar } from "notistack";
import React, { ChangeEvent, useState } from "react";
import { DatePicker } from "react-nice-dates";
import Button from "../components/UI/Button";
import { ProgressContainer } from "../components/UI/ProgressContainer";
import { Settings } from "../ostertalertypes";
import { useSettingsContext } from "../SettingsContext";
import { updateSettings } from "../utils/database";
import { dateToUTC } from "../utils/dateutils";
import { handleError, InputState } from "../utils/utils";

const PageSettings: React.FC=() => {

  const { enqueueSnackbar } = useSnackbar();
  const settingsCtx = useSettingsContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tmpSetting, setTmpSetting] = useState<Settings>(settingsCtx.settings);
  const [dayRestrictionError, setDayRestrictionError] = useState<InputState>({ errorMessage: undefined, value: "" });


  async function onBtnClickHandler(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    try {
      setIsLoading(true);
      await updateSettings(tmpSetting);
      settingsCtx.setSettings(tmpSetting);
      setIsLoading(false);
      setDayRestrictionError({errorMessage: undefined, value:""});
      enqueueSnackbar("Einstellungen gespeichert", {
        variant: "success",
        persist: false,
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
    } catch (error) {
      handleError("Fehler beim Speichern der Einstellungen", error, enqueueSnackbar, setIsLoading);
    }
  }

  const setDate = (selectedDate: Date | null) => {
    if (settingsCtx.settings && selectedDate) {
      const newSettings = cloneDeep(tmpSetting);
      newSettings.reservation.maxAllowedBookingDate = Timestamp.fromMillis(dateToUTC(selectedDate));
      setTmpSetting(newSettings);
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const newSettings = cloneDeep(tmpSetting);
    if (event.target.name === "calendarpublic") {
      newSettings.reservation.allocationCheckerPublic = checked;
    }
    else if (event.target.name === "sendmails") {
      newSettings.reservation.emailOnReservationUpdate = checked;
    }
    setTmpSetting(newSettings);
  }

  function onDayRestrictionChange(event: ChangeEvent<HTMLInputElement>) {
    let errorMessage: string | undefined = undefined;
    const days: number = Number(event.currentTarget.value.trim());
    if (isNaN(days)) {
      errorMessage = "Nur Zahlen erlaubt. Wert 0 schaltet die Limitierung ab.";
    } else if (days < 0) {
      errorMessage = "Nur postive Zahlen erlaubt. Wert 0 schaltet die Limitierung ab.";
    } else {
      errorMessage = undefined;
    }
    setDayRestrictionError({errorMessage: errorMessage, value:""})
    if (errorMessage === undefined) {
      const newSettings = cloneDeep(tmpSetting);
      newSettings.reservation.bookingRestrictionDays = days;
      setTmpSetting(newSettings);
    }
  }

  return (
    <ProgressContainer isLoading={isLoading}>
      <h1 className="text-2xl mt-0 mb-10 text-gray-500">Einstellungen</h1>
      <form noValidate autoComplete="off">
        <FormControlLabel label="Belegungskalender öffentlich einsehbar." control={
          <Checkbox name="calendarpublic" checked={tmpSetting.reservation.allocationCheckerPublic} onChange={handleChange} />}/>
        <FormControlLabel label="Sende E-Mails an Benutzer bei Erstellung/Änderung von Buchungen." control={
          <Checkbox name="sendmails" checked={tmpSetting.reservation.emailOnReservationUpdate} onChange={handleChange} />}/>

        <TextField value={tmpSetting.reservation.bookingRestrictionDays} onChange={onDayRestrictionChange} label="Buchungsanfragen durch Gast limitieren auf alle"
          InputProps={{
            endAdornment: <InputAdornment position="end">Tage</InputAdornment>,
          }}
          className="w-full mt-5"
          helperText={dayRestrictionError.errorMessage}
          error={dayRestrictionError.errorMessage? true : false}/>
        <DatePicker date={tmpSetting.reservation.maxAllowedBookingDate.toDate()} onDateChange={setDate} locale={de}>
          {({ inputProps, focused }) => (
            <TextField className="w-full mt-5" key="anreise" label="Maximales Buchungsdatum:" id="labelFrom" inputProps={inputProps}/>
          )}
        </DatePicker>
        <div className={`grid justify-items-end mt-10 gap-2`}>
          <Button onClick={onBtnClickHandler} disabled={isLoading} className="w-full">Speichern</Button>
        </div>
      </form>
    </ProgressContainer>
  );
}

export default PageSettings;
